/*
 * The portal's look (PT-06, docs/15).
 *
 * DESIGN.md's tokens and faces, from the desktop's own files so the two never
 * drift: the same sage and mint, the same three bundled faces. What it does not
 * take is the desktop's density override. Customers order from phones, so this
 * uses the source system's comfortable spacing and 44px touch targets, and one
 * column that widens on a desk.
 *
 * Light only, as the desktop: DESIGN.md's adoption notes.
 */

@import '../../../desktop/src/renderer/styles/fonts.css';
@import '../../../desktop/src/renderer/styles/tokens.css';

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
}

body {
  margin: 0;
  background: var(--canvas-sunken);
  color: var(--ink);
  font-family: var(--font-body);
  font-size: var(--text-body-lg);
  line-height: var(--leading-body);
}

h1,
h2,
h3 {
  font-family: var(--font-display);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--track-heading);
  line-height: var(--leading-heading);
  margin: 0;
}

h1 {
  font-size: 26px;
}

h2 {
  font-size: 21px;
}

p {
  margin: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---- Layout -------------------------------------------------------------- */

.shell {
  max-width: 760px;
  margin: 0 auto;
  padding: var(--space-lg) var(--space-md) 120px;
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.shell--narrow {
  max-width: 440px;
  padding-top: var(--space-2xl);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.stack--tight {
  gap: var(--space-xs);
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-md);
}

/* A long trading name wraps; "Sign out" beside it does not. */
.top > .button {
  flex-shrink: 0;
  white-space: nowrap;
}

.eyebrow {
  font-family: var(--font-mono);
  font-size: var(--text-caption);
  letter-spacing: var(--track-chip);
  text-transform: uppercase;
  color: var(--action);
}

.lede {
  color: var(--ink-muted);
}

.fine {
  font-size: var(--text-caption);
  line-height: var(--leading-caption);
}

.faint {
  color: var(--ink-faint);
}

/* ---- Controls: 44px targets ---------------------------------------------- */

.button {
  min-height: 44px;
  padding: 0 var(--space-lg);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-button);
  background: var(--canvas);
  color: var(--ink);
  font-family: var(--font-display);
  font-size: var(--text-body-lg);
  font-weight: var(--weight-medium);
  cursor: pointer;
}

.button:hover:not(:disabled) {
  box-shadow: var(--action-halo);
}

.button:focus-visible,
.input:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
  border-color: var(--focus);
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button--primary {
  background: var(--action-primary);
  border-color: var(--action-primary);
  color: var(--ink-on-accent);
}

.button--quiet {
  border-color: transparent;
  background: transparent;
  color: var(--action);
}

.button--danger {
  background: var(--danger);
  border-color: var(--danger);
  color: var(--ink-on-fill);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.field__label {
  font-weight: var(--weight-medium);
}

.field__optional {
  color: var(--ink-faint);
  font-weight: var(--weight-regular);
  font-size: var(--text-caption);
}

.input {
  min-height: 44px;
  width: 100%;
  padding: var(--space-sm) var(--space-dense);
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius-input);
  background: var(--canvas);
  color: var(--ink);
  /* 16px keeps a phone from zooming into the field. */
  font: inherit;
  font-size: 16px;
}

.input[aria-invalid='true'] {
  border-color: var(--tone-bad-ink);
}

.input--code {
  font-family: var(--font-mono);
  font-size: 22px;
  letter-spacing: 0.3em;
}

/* ---- Tabs ---------------------------------------------------------------- */

.tabs {
  display: flex;
  gap: var(--space-xs);
  border-bottom: 1px solid var(--hairline);
}

.tabs__tab {
  min-height: 44px;
  padding: 0 var(--space-md);
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: var(--ink-muted);
  font-family: var(--font-display);
  font-size: var(--text-body-lg);
  cursor: pointer;
}

.tabs__tab[aria-current='page'] {
  color: var(--ink);
  border-bottom-color: var(--action-strong);
  font-weight: var(--weight-semibold);
}

/* ---- Notices and pills ----------------------------------------------------- */

.notice {
  padding: var(--space-dense) var(--space-md);
  border-radius: var(--radius-sm);
  border: 1px solid var(--tone-neutral-edge);
  background: var(--tone-neutral-fill);
}

.notice--bad {
  border-color: var(--tone-bad-edge);
  background: var(--tone-bad-fill);
  color: var(--tone-bad-ink);
}

.notice--warn {
  border-color: var(--tone-warn-edge);
  background: var(--tone-warn-fill);
  color: var(--tone-warn-ink);
}

.notice--good {
  border-color: var(--tone-good-edge);
  background: var(--tone-good-fill);
  color: var(--tone-good-ink);
}

.pill {
  display: inline-block;
  padding: 2px var(--space-sm);
  border-radius: var(--radius-full);
  border: 1px solid var(--tone-neutral-edge);
  background: var(--tone-neutral-fill);
  color: var(--tone-neutral-ink);
  font-family: var(--font-mono);
  font-size: var(--text-caption);
}

.pill--warn {
  border-color: var(--tone-warn-edge);
  background: var(--tone-warn-fill);
  color: var(--tone-warn-ink);
}

.pill--good {
  border-color: var(--tone-good-edge);
  background: var(--tone-good-fill);
  color: var(--tone-good-ink);
}

.pill--bad {
  border-color: var(--tone-bad-edge);
  background: var(--tone-bad-fill);
  color: var(--tone-bad-ink);
}

/* ---- The list ------------------------------------------------------------ */

/* Search and category: one column on a phone, side by side on a desk. */
.filters {
  display: grid;
  gap: var(--space-sm);
}

.items,
.reviews,
.orders,
.lines {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.items,
.reviews,
.orders {
  gap: var(--space-sm);
}

.item,
.review,
.order {
  display: grid;
  gap: var(--space-sm);
  padding: var(--space-md);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-lg);
  background: var(--canvas);
  box-shadow: var(--shadow-sm);
}

.item {
  grid-template-columns: minmax(0, 1fr) 96px;
  grid-template-areas:
    'text quantity'
    'figures quantity';
  align-items: center;
}

.item__text,
.review__text {
  grid-area: text;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* A long botanical name wraps rather than widening the page. */
.item__name,
.line__name {
  overflow-wrap: anywhere;
}

.item__name {
  font-weight: var(--weight-semibold);
}

.item__figures {
  grid-area: figures;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-md);
  align-items: baseline;
}

.item__price,
.line__figure,
.review__figure,
.total {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
}

.quantity {
  grid-area: quantity;
}

.quantity .input {
  text-align: right;
  font-family: var(--font-mono);
}

/* Beside the box they are about, across the whole card. */
.item__problem,
.review__gone {
  grid-column: 1 / -1;
  font-size: var(--text-caption);
}

.item__problem {
  color: var(--tone-bad-ink);
}

.review__gone {
  color: var(--tone-warn-ink);
  font-weight: var(--weight-medium);
}

/* The review: the name across the top, then the box, the line's cost, Remove. */
.review {
  grid-template-columns: 96px minmax(0, 1fr) auto;
  grid-template-areas:
    'text text text'
    'quantity figure remove';
  align-items: center;
}

.review__figure {
  grid-area: figure;
  text-align: right;
}

.review__remove {
  grid-area: remove;
  padding: 0 var(--space-sm);
}

.order__head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--space-sm);
  align-items: center;
}

.line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-xs) var(--space-md);
  padding: var(--space-sm) 0;
  border-bottom: 1px solid var(--divider-soft);
}

.line__figure {
  text-align: right;
}

/* What was asked, and once confirmed, what the nursery will send. */
.line__quantities {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs) var(--space-md);
  font-family: var(--font-mono);
  font-size: var(--text-caption);
  color: var(--ink-muted);
}

.line__confirmed {
  color: var(--ink);
  font-weight: var(--weight-semibold);
}

.line__confirmed--short {
  color: var(--tone-warn-ink);
}

.total {
  font-size: 18px;
  font-weight: var(--weight-semibold);
  text-align: right;
}

/* The basket stays in reach of a thumb. */
.basket {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-dense)
    max(var(--space-md), calc((100vw - 760px) / 2 + var(--space-md)));
  background: var(--canvas);
  border-top: 1px solid var(--hairline);
  box-shadow: var(--shadow-md);
  font-family: var(--font-mono);
}

.basket > span {
  min-width: 0;
  font-size: var(--text-body);
}

.basket > .button {
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .filters {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  }

  .item {
    grid-template-columns: minmax(0, 1fr) auto 110px;
    grid-template-areas: 'text figures quantity';
  }

  .review {
    grid-template-columns: minmax(0, 1fr) 110px 120px auto;
    grid-template-areas: 'text quantity figure remove';
  }

  .item__figures {
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
  }
}
